import { MONTHS, type CostCenter, type SubmissionVersion, type TaskRow, type WorkspaceData } from "../types";

export type ReportCell = string | number;

function formatDate(value: string | null) {
  if (!value) return "";
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  return new Intl.DateTimeFormat("es-PE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function versionLabel(workspace: WorkspaceData, task: TaskRow, draft = false) {
  if (draft) return "PROPUESTA (BORRADOR)";
  if (workspace.cycle.status !== "closed") return "PROPUESTA ENVIADA";
  return `MOD ${String(task.baselineVersionNumber + 1).padStart(2, "0")}`;
}

export function buildVersionReportRows(workspace: WorkspaceData, center: CostCenter, includeDraft: boolean): ReportCell[][] {
  const header: ReportCell[] = [
    "Centro", "Centro de costo", "Actividad", "Código de tarea", "Tarea", "Unidad",
    "Versión actualización", ...MONTHS, "TOTAL", "SUSTENTO", "DOCUMENTO", "Usuario registro", "FECHA",
  ];
  const versions: Array<SubmissionVersion & { draft?: boolean }> = [...workspace.versions];

  if (includeDraft && workspace.submissionStatus === "draft") {
    versions.push({
      id: "draft",
      versionNumber: Math.max(0, ...workspace.versions.map((version) => version.versionNumber)) + 1,
      justification: "Borrador pendiente de cierre",
      documentReference: "Pendiente de registrar",
      createdByName: workspace.profile.fullName,
      createdAt: new Date().toISOString(),
      draft: true,
      targets: workspace.tasks
        .filter((task) => task.modified.some((value) => value !== null))
        .map((task) => ({ taskId: task.id, values: task.baseline.map((value, index) => task.modified[index] ?? value), justification: workspace.taskJustifications[task.id]?.trim() || "Pendiente de registrar" })),
    });
  }

  const taskIds = new Set(versions.flatMap((version) => version.targets.map((target) => target.taskId)));
  const reportVersions = workspace.cycle.status === "closed" && versions.length
    ? [versions.at(-1)!]
    : versions;
  const rows: ReportCell[][] = [header];
  for (const task of workspace.tasks.filter((item) => taskIds.has(item.id))) {
    const common: ReportCell[] = [center.code, center.name, task.activityCode, task.taskCode, task.taskName, task.unit];
    const sourceVersions = task.sourceVersions?.length ? task.sourceVersions : [{
      versionNumber: task.baselineVersionNumber,
      sourceDocument: workspace.cycle.sourceFile,
      sourceDate: workspace.cycle.sourceImportedAt,
      unit: task.unit,
      values: task.baseline,
    }];
    for (const sourceVersion of sourceVersions) {
      rows.push([
        center.code, center.name, task.activityCode, task.taskCode, task.taskName, sourceVersion.unit || task.unit,
        `MOD ${String(sourceVersion.versionNumber).padStart(2, "0")}`, ...sourceVersion.values,
        sourceVersion.values.reduce((total, value) => total + value, 0),
        sourceVersion.versionNumber === 0 ? "Programación inicial" : "Actualización histórica",
        sourceVersion.sourceDocument, "Importación histórica", formatDate(sourceVersion.sourceDate),
      ]);
    }

    for (const version of reportVersions.sort((a, b) => a.versionNumber - b.versionNumber)) {
      const snapshot = version.targets.find((target) => target.taskId === task.id);
      if (!snapshot) continue;
      rows.push([
        ...common, versionLabel(workspace, task, version.draft), ...snapshot.values,
        snapshot.values.reduce((total, value) => total + value, 0),
        snapshot.justification ?? version.justification, version.documentReference, version.createdByName, formatDate(version.createdAt),
      ]);
    }
  }
  return rows;
}
